import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Section, SerifH, Eyebrow, PrimaryButton } from "../components/ui-kit/Primitives";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import api from "../lib/api";

const POLL_INTERVAL_MS = 2500;
const MAX_ATTEMPTS = 20; // ~50 seconds

const PaymentSuccess = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("polling"); // polling | paid | timeout | error
  const [record, setRecord] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        setRecord(data);
        if (data.payment_status === "paid") {
          setState("paid");
          return;
        }
        if (attempts.current >= MAX_ATTEMPTS) {
          setState("timeout");
          return;
        }
        setTimeout(tick, POLL_INTERVAL_MS);
      } catch (e) {
        if (attempts.current >= MAX_ATTEMPTS) {
          setState("error");
        } else {
          setTimeout(tick, POLL_INTERVAL_MS);
        }
      }
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  return (
    <div className="bg-brand-cream min-h-screen" data-testid="payment-success-page">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-brand-green flex items-center justify-center text-brand-gold font-serif text-xl">K</div>
          <div className="leading-tight">
            <div className="font-serif text-lg text-brand-green">KBX Pathways</div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-brand-sage">To Wellness</div>
          </div>
        </Link>
      </div>
      <Section className="pt-6">
        <div className="max-w-2xl mx-auto text-center">
          {state === "polling" && (
            <>
              <Loader2 className="w-14 h-14 text-brand-green mx-auto animate-spin" />
              <Eyebrow>Confirming your payment</Eyebrow>
              <SerifH as="h1" className="text-4xl sm:text-5xl">
                Just a moment while we confirm with Stripe…
              </SerifH>
              <p className="mt-4 text-slate-600">This usually takes just a few seconds.</p>
            </>
          )}
          {state === "paid" && (
            <>
              <CheckCircle2 className="w-16 h-16 text-brand-green mx-auto" />
              <Eyebrow color="gold">Payment received</Eyebrow>
              <SerifH as="h1" className="text-4xl sm:text-5xl">
                Welcome to KBX Pathways.
              </SerifH>
              <p className="mt-5 text-slate-600 leading-relaxed">
                Thank you for reserving your spot in{" "}
                <span className="font-medium text-brand-green">{record?.package_label}</span>. A
                member of our team will reach out shortly to schedule your onboarding call and next
                steps.
              </p>
              {record?.amount != null && (
                <div className="mt-6 inline-block bg-white border border-brand-green/15 rounded-2xl px-6 py-4">
                  <div className="text-xs tracking-[0.2em] uppercase text-brand-sage">Amount Paid</div>
                  <div className="font-serif text-3xl text-brand-green mt-1">
                    ${Number(record.amount).toLocaleString()}
                  </div>
                </div>
              )}
              <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                <PrimaryButton to="/" testid="success-home">Return Home</PrimaryButton>
              </div>
            </>
          )}
          {state === "timeout" && (
            <>
              <AlertCircle className="w-14 h-14 text-brand-gold mx-auto" />
              <Eyebrow>Still processing</Eyebrow>
              <SerifH as="h1" className="text-4xl sm:text-5xl">
                Your payment is still processing.
              </SerifH>
              <p className="mt-4 text-slate-600 leading-relaxed">
                Some payment methods (like Affirm) take a little longer to confirm. You'll receive
                an email receipt from Stripe as soon as it's finalized. If you have questions, email{" "}
                <a className="underline" href="mailto:info@kbxpathwaystowellness.com">
                  info@kbxpathwaystowellness.com
                </a>
                .
              </p>
              <div className="mt-8">
                <PrimaryButton to="/" testid="timeout-home">Return Home</PrimaryButton>
              </div>
            </>
          )}
          {state === "error" && (
            <>
              <AlertCircle className="w-14 h-14 text-red-500 mx-auto" />
              <Eyebrow>Unable to confirm</Eyebrow>
              <SerifH as="h1" className="text-4xl sm:text-5xl">
                We couldn't confirm your payment.
              </SerifH>
              <p className="mt-4 text-slate-600 leading-relaxed">
                If you were charged, please email{" "}
                <a className="underline" href="mailto:info@kbxpathwaystowellness.com">
                  info@kbxpathwaystowellness.com
                </a>{" "}
                with your Stripe receipt and we'll make it right.
              </p>
              <div className="mt-8">
                <PrimaryButton to="/" testid="error-home">Return Home</PrimaryButton>
              </div>
            </>
          )}
        </div>
      </Section>
    </div>
  );
};

export default PaymentSuccess;
